import { PERMISSIONS, type Permission } from '../../permissions/permissions';
import { APP_MODULES, type AppModule, HANDLING_PARTY_MODULES } from './app';
import { PERSONAL_ACCESS_TOKEN_UI_EXPIRY_DAYS } from './personal-access-token';

/** Default lifetime assigned to newly created and rotated Service API tokens. */
export const SERVICE_API_TOKEN_DEFAULT_LIFETIME_DAYS = 90 as const;

/** Hard write-time lifetime ceiling for Service API tokens. */
export const SERVICE_API_TOKEN_MAX_LIFETIME_DAYS = 365 as const;

/**
 * UI presets are constrained by the Service API policy even if the separate
 * Personal Access Token policy later gains longer-lived options.
 */
export const SERVICE_API_TOKEN_UI_EXPIRY_DAYS = PERSONAL_ACCESS_TOKEN_UI_EXPIRY_DAYS.filter(
    (days) => days <= SERVICE_API_TOKEN_MAX_LIFETIME_DAYS,
);

/** Advance warning window used by both the UI and administrator email job. */
export const SERVICE_API_TOKEN_EXPIRY_WARNING_DAYS = 30 as const;

/**
 * Affected legacy tokens used within one default lifetime are treated as live
 * during remediation so their organisation receives the longer grace period.
 */
export const SERVICE_API_TOKEN_RECENT_USE_CUTOFF_DAYS = 90 as const;

/**
 * Service API Token Prefix
 * All service API tokens start with this prefix for easy identification.
 * Format: cu_<32-char-random> (e.g., cu_a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6)
 */
export const SERVICE_API_TOKEN_PREFIX = 'cu_' as const;

/**
 * Service API token scopes mapped to existing permissions.
 * Uses the same permission strings for consistency with the auth system.
 *
 * These scopes define what actions a service API token can perform.
 * Each scope corresponds to an existing permission in the system.
 */
export const SERVICE_API_TOKEN_SCOPES = {
    // Claim scopes
    CLAIM_LIST: PERMISSIONS.CLAIM_LIST, // 'claim:list'
    CLAIM_READ: PERMISSIONS.CLAIM_READ, // 'claim:read'
    CLAIM_CREATE: PERMISSIONS.CLAIM_CREATE, // 'claim:create' — lodgement v2 via POST /lodgements
    CLAIM_UPDATE: PERMISSIONS.CLAIM_UPDATE, // 'claim:update'
    // Incident scopes
    INCIDENT_CREATE: PERMISSIONS.INCIDENT_CREATE, // 'incident:create'
    INCIDENT_UPDATE: PERMISSIONS.INCIDENT_UPDATE, // 'incident:update'
    // Complaint scopes
    COMPLAINT_CREATE: PERMISSIONS.COMPLAINT_CREATE, // 'complaint:create'
    COMPLAINT_READ: PERMISSIONS.COMPLAINT_READ, // 'complaint:read'
    COMPLAINT_LIST: PERMISSIONS.COMPLAINT_LIST, // 'complaint:list'
    COMPLAINT_UPDATE: PERMISSIONS.COMPLAINT_UPDATE, // 'complaint:update'
    // Handling party scopes
    HANDLING_PARTY_LIST: PERMISSIONS.HANDLING_PARTY_LIST, // 'handling_party:list'
    HANDLING_PARTY_CREATE: PERMISSIONS.HANDLING_PARTY_CREATE, // 'handling_party:create'
    HANDLING_PARTY_UPDATE: PERMISSIONS.HANDLING_PARTY_UPDATE, // 'handling_party:update'
    // User scopes
    USER_INVITE: PERMISSIONS.USER_CREATE, // 'user:create'
    USER_READ: PERMISSIONS.USER_READ, // 'user:read'
    USER_UPDATE: PERMISSIONS.USER_UPDATE, // 'user:update'
    // Data export scopes
    CLAIM_EXPORT: PERMISSIONS.CLAIM_EXPORT, // 'claim:export'
    INCIDENT_EXPORT: PERMISSIONS.INCIDENT_EXPORT, // 'incident:export'
    COMPLAINT_EXPORT: PERMISSIONS.COMPLAINT_EXPORT, // 'complaint:export'
    OBLIGATION_EXPORT: PERMISSIONS.OBLIGATION_EXPORT, // 'obligation:export'
    RISK_EXPORT: PERMISSIONS.RISK_EXPORT, // 'risk:export'
    HANDLING_PARTY_EXPORT: PERMISSIONS.HANDLING_PARTY_EXPORT, // 'handling_party:export'
} as const satisfies Record<string, Permission>;

/**
 * Type representing valid service API token scopes.
 */
export type ServiceApiTokenScope = (typeof SERVICE_API_TOKEN_SCOPES)[keyof typeof SERVICE_API_TOKEN_SCOPES];

/**
 * Array of all valid scope values for Zod validation.
 */
export const SERVICE_API_TOKEN_SCOPE_VALUES = Object.values(SERVICE_API_TOKEN_SCOPES);

/**
 * Human-readable labels for each scope (used in UI).
 */
export const SERVICE_API_TOKEN_SCOPE_LABELS = {
    [SERVICE_API_TOKEN_SCOPES.CLAIM_LIST]: 'List claims',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_READ]: 'Read claims',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_CREATE]: 'Lodge claims',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_UPDATE]: 'Update claims',
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_CREATE]: 'Create incidents',
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_UPDATE]: 'Update incidents',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_CREATE]: 'Create complaints',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_READ]: 'Read complaints',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_LIST]: 'List complaints',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_UPDATE]: 'Update complaints',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_LIST]: 'List handling parties',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_CREATE]: 'Create handling parties',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_UPDATE]: 'Update handling parties',
    [SERVICE_API_TOKEN_SCOPES.USER_INVITE]: 'Invite users',
    [SERVICE_API_TOKEN_SCOPES.USER_READ]: 'List users',
    [SERVICE_API_TOKEN_SCOPES.USER_UPDATE]: 'Update users',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_EXPORT]: 'Export claims',
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_EXPORT]: 'Export incidents',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_EXPORT]: 'Export complaints',
    [SERVICE_API_TOKEN_SCOPES.OBLIGATION_EXPORT]: 'Export obligations',
    [SERVICE_API_TOKEN_SCOPES.RISK_EXPORT]: 'Export risks',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_EXPORT]: 'Export handling parties',
} as const satisfies Record<ServiceApiTokenScope, string>;

/**
 * Descriptions for each scope (used in UI tooltips/documentation).
 */
export const SERVICE_API_TOKEN_SCOPE_DESCRIPTIONS = {
    [SERVICE_API_TOKEN_SCOPES.CLAIM_LIST]: 'Allows listing claims with pagination and filtering via the API',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_READ]: 'Allows retrieving individual claim details via the API',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_CREATE]: 'Allows lodging claims through a lodgement link via the API',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_UPDATE]: 'Allows updating existing claims via the API',
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_CREATE]: 'Allows creating new incidents via the API',
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_UPDATE]: 'Allows updating existing incidents via the API',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_CREATE]: 'Allows creating new complaints via the API',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_READ]: 'Allows retrieving individual complaint details via the API',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_LIST]: 'Allows listing complaints with pagination and filtering via the API',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_UPDATE]: 'Allows updating existing complaints via the API',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_LIST]:
        'Allows listing handling parties with pagination and filtering via the API',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_CREATE]: 'Allows creating new handling parties via the API',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_UPDATE]: 'Allows updating existing handling parties via the API',
    [SERVICE_API_TOKEN_SCOPES.USER_INVITE]: 'Allows inviting new users to the organisation via the API',
    [SERVICE_API_TOKEN_SCOPES.USER_READ]:
        'Allows listing enabled users in the organisation via the API (for owner assignment)',
    [SERVICE_API_TOKEN_SCOPES.USER_UPDATE]: 'Allows updating existing users in the organisation via the API',
    [SERVICE_API_TOKEN_SCOPES.CLAIM_EXPORT]: 'Allows bulk export of claims with incremental sync support',
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_EXPORT]: 'Allows bulk export of incidents with incremental sync support',
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_EXPORT]: 'Allows bulk export of complaints with incremental sync support',
    [SERVICE_API_TOKEN_SCOPES.OBLIGATION_EXPORT]: 'Allows bulk export of obligations with incremental sync support',
    [SERVICE_API_TOKEN_SCOPES.RISK_EXPORT]: 'Allows bulk export of risks with incremental sync support',
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_EXPORT]:
        'Allows bulk export of handling parties with incremental sync support',
} as const satisfies Record<ServiceApiTokenScope, string>;

/**
 * Module condition a scope carries: `anyOf` requires at least one listed module on the
 * organisation (matching `meta.appModules` / `staticData.modules` semantics); `null` means the
 * scope is module-independent and stays usable even when `enabledModules` is empty.
 */
export type ServiceApiScopeModulePolicy = { readonly anyOf: readonly AppModule[] } | null;

const CLAIM_ONLY = { anyOf: [APP_MODULES.CLAIM] } as const satisfies ServiceApiScopeModulePolicy;
const COMPLIANCE_ONLY = { anyOf: [APP_MODULES.COMPLIANCE] } as const satisfies ServiceApiScopeModulePolicy;
const RISK_ONLY = { anyOf: [APP_MODULES.RISK] } as const satisfies ServiceApiScopeModulePolicy;
const HANDLING_PARTY_ANY = { anyOf: HANDLING_PARTY_MODULES } as const satisfies ServiceApiScopeModulePolicy;

/**
 * Module entitlement per scope (CUR-1145). The Service API path never runs `authProcedure`, so
 * this table is the module gate for service tokens: enforced at request time in
 * `serviceTokenProcedure` for the endpoint's required scope, at token create/update, and used by
 * the scope selector to offer only eligible scopes. Exhaustive over the registry — `satisfies`
 * fails to compile when a scope is added without a policy (including the unmounted
 * `obligation:export`).
 *
 * Domain scopes follow their authenticated equivalents (`server/src/routes/<domain>`): claims →
 * `claim`; incidents, complaints and obligations → `compliance`; risks → `risk`. Handling parties
 * are shared across those domains, so any of `HANDLING_PARTY_MODULES` qualifies. User scopes are
 * module-independent: team management is a platform capability every org needs, and the
 * authenticated user routes carry no module gate either. Role payloads on user invite/update are
 * still module-bound via `ROLE_METADATA[...].requiredModules`.
 */
export const SERVICE_API_SCOPE_MODULES = {
    [SERVICE_API_TOKEN_SCOPES.CLAIM_LIST]: CLAIM_ONLY,
    [SERVICE_API_TOKEN_SCOPES.CLAIM_READ]: CLAIM_ONLY,
    [SERVICE_API_TOKEN_SCOPES.CLAIM_CREATE]: CLAIM_ONLY,
    [SERVICE_API_TOKEN_SCOPES.CLAIM_UPDATE]: CLAIM_ONLY,
    [SERVICE_API_TOKEN_SCOPES.CLAIM_EXPORT]: CLAIM_ONLY,
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_CREATE]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_UPDATE]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.INCIDENT_EXPORT]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_CREATE]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_READ]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_LIST]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_UPDATE]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.COMPLAINT_EXPORT]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.OBLIGATION_EXPORT]: COMPLIANCE_ONLY,
    [SERVICE_API_TOKEN_SCOPES.RISK_EXPORT]: RISK_ONLY,
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_LIST]: HANDLING_PARTY_ANY,
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_CREATE]: HANDLING_PARTY_ANY,
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_UPDATE]: HANDLING_PARTY_ANY,
    [SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_EXPORT]: HANDLING_PARTY_ANY,
    [SERVICE_API_TOKEN_SCOPES.USER_INVITE]: null,
    [SERVICE_API_TOKEN_SCOPES.USER_READ]: null,
    [SERVICE_API_TOKEN_SCOPES.USER_UPDATE]: null,
} as const satisfies Record<ServiceApiTokenScope, ServiceApiScopeModulePolicy>;

/**
 * Whether an organisation with `enabledModules` may hold and exercise `scope`.
 * A `null` policy is always eligible, including for an empty module list.
 */
export function isServiceApiScopeEligible(scope: ServiceApiTokenScope, enabledModules: readonly string[]): boolean {
    const policy: ServiceApiScopeModulePolicy = SERVICE_API_SCOPE_MODULES[scope];
    return policy === null || policy.anyOf.some((module) => enabledModules.includes(module));
}

/** Scopes from `scopes` that the organisation's modules do not qualify, in the given order. */
export function getIneligibleServiceApiScopes(
    scopes: readonly ServiceApiTokenScope[],
    enabledModules: readonly string[],
): ServiceApiTokenScope[] {
    return scopes.filter((scope) => !isServiceApiScopeEligible(scope, enabledModules));
}

/** Every registered scope the organisation's modules qualify, in registry order. */
export function getEligibleServiceApiScopes(enabledModules: readonly string[]): ServiceApiTokenScope[] {
    return SERVICE_API_TOKEN_SCOPE_VALUES.filter((scope) => isServiceApiScopeEligible(scope, enabledModules));
}

/**
 * Maximum number of scopes that can be assigned to a single token.
 * Enforces least-privilege principle.
 */
export const SERVICE_API_TOKEN_MAX_SCOPES = 10 as const;

/**
 * Maximum number of IP addresses or CIDR ranges that can be assigned to a single token.
 * Keeps per-request allowlist verification bounded.
 */
export const SERVICE_API_TOKEN_MAX_IP_ALLOWLIST_ENTRIES = 100 as const;

/**
 * Default rate-limit policy applied to every Service API endpoint, bucketed per token
 * (key `svc:<tokenId>`). Routes may override the ceiling via `meta.limiter`, and heavy
 * endpoints charge a higher per-request `cost` (see `EXPORT_COST`). Enforced in the
 * `rateLimitMiddleware` service-token branch (server/src/trpc/procedures.ts).
 */
export const SERVICE_API_RATE_LIMIT = {
    /** Points per window, per token, per endpoint path. */
    MAX_PER_WINDOW: 600,
    /**
     * Window length in milliseconds. FIXED window, not sliding — rate-limiter-flexible
     * opens the bucket on the first request and expires it `WINDOW_MS` later.
     */
    WINDOW_MS: 60_000,
    /** Points charged per bulk-export request (heavier than the default cost of 1). */
    EXPORT_COST: 5,
} as const;

/**
 * Scope groups for UI display. Organises the 21 scopes into 6 logical categories.
 * Array order determines rendering order.
 *
 * Type safety: `satisfies` ensures all scope values are valid ServiceApiTokenScope.
 * Runtime invariant (tested in CI): all SERVICE_API_TOKEN_SCOPE_VALUES appear in exactly one group.
 */
export const SERVICE_API_TOKEN_SCOPE_GROUPS = [
    {
        key: 'claims',
        label: 'Claims',
        scopes: [
            SERVICE_API_TOKEN_SCOPES.CLAIM_LIST,
            SERVICE_API_TOKEN_SCOPES.CLAIM_READ,
            SERVICE_API_TOKEN_SCOPES.CLAIM_CREATE,
            SERVICE_API_TOKEN_SCOPES.CLAIM_UPDATE,
        ],
    },
    {
        key: 'incidents',
        label: 'Incidents',
        scopes: [SERVICE_API_TOKEN_SCOPES.INCIDENT_CREATE, SERVICE_API_TOKEN_SCOPES.INCIDENT_UPDATE],
    },
    {
        key: 'complaints',
        label: 'Complaints',
        scopes: [
            SERVICE_API_TOKEN_SCOPES.COMPLAINT_CREATE,
            SERVICE_API_TOKEN_SCOPES.COMPLAINT_READ,
            SERVICE_API_TOKEN_SCOPES.COMPLAINT_LIST,
            SERVICE_API_TOKEN_SCOPES.COMPLAINT_UPDATE,
        ],
    },
    {
        key: 'handlingParties',
        label: 'Handling parties',
        scopes: [
            SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_LIST,
            SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_CREATE,
            SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_UPDATE,
        ],
    },
    {
        key: 'users',
        label: 'Users',
        scopes: [
            SERVICE_API_TOKEN_SCOPES.USER_INVITE,
            SERVICE_API_TOKEN_SCOPES.USER_READ,
            SERVICE_API_TOKEN_SCOPES.USER_UPDATE,
        ],
    },
    {
        key: 'dataExport',
        label: 'Data export',
        scopes: [
            SERVICE_API_TOKEN_SCOPES.CLAIM_EXPORT,
            SERVICE_API_TOKEN_SCOPES.INCIDENT_EXPORT,
            SERVICE_API_TOKEN_SCOPES.COMPLAINT_EXPORT,
            SERVICE_API_TOKEN_SCOPES.OBLIGATION_EXPORT,
            SERVICE_API_TOKEN_SCOPES.RISK_EXPORT,
            SERVICE_API_TOKEN_SCOPES.HANDLING_PARTY_EXPORT,
        ],
    },
] as const satisfies ReadonlyArray<{
    key: string;
    label: string;
    scopes: readonly ServiceApiTokenScope[];
}>;

/**
 * Token length configuration.
 * The random portion of the token (after the prefix).
 */
export const SERVICE_API_TOKEN_RANDOM_LENGTH = 32 as const;

/**
 * Full token length (prefix + random portion).
 * Computed as: prefix (3 chars: "cu_") + random (32 chars) = 35 chars total
 */
export const SERVICE_API_TOKEN_FULL_LENGTH = 35 as const;

/**
 * Check if a given string is a valid service API token format.
 * Does not verify the token exists or is active - only checks the format.
 */
export function isServiceApiTokenFormat(token: string): boolean {
    return (
        typeof token === 'string' &&
        token.startsWith(SERVICE_API_TOKEN_PREFIX) &&
        token.length === SERVICE_API_TOKEN_FULL_LENGTH
    );
}

/**
 * Check if a scope is a valid service API token scope.
 */
export function isValidServiceApiTokenScope(scope: string): scope is ServiceApiTokenScope {
    return SERVICE_API_TOKEN_SCOPE_VALUES.includes(scope as ServiceApiTokenScope);
}
